import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Plus, Trash2, Upload, X, ImagePlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const MenuManager = ({ headers, items, categories, refresh }) => {
  const [query, setQuery] = useState("");
  const [edits, setEdits] = useState({});
  const [imageFor, setImageFor] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState({
    name: "",
    desc: "",
    price: "",
    category: "",
    newCategory: "",
    image: "",
  });
  const rowFileRef = useRef(null);
  const newFileRef = useRef(null);
  const uploadTarget = useRef(null);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) => i.name.toLowerCase().includes(q) || i.cat.toLowerCase().includes(q)
    );
  }, [items, query]);

  const setEdit = (name, patch) =>
    setEdits((prev) => ({ ...prev, [name]: { ...prev[name], ...patch } }));

  const saveItem = async (item) => {
    const e = edits[item.name] || {};
    const payload = { name: item.name };
    if (e.price !== undefined && parseInt(e.price, 10) !== item.price) {
      const p = parseInt(e.price, 10);
      if (!p || p <= 0) {
        toast.error("Enter a valid price in rupees");
        return;
      }
      payload.price = p;
    }
    if (e.category !== undefined && e.category !== item.cat) {
      const cat = e.category === "__new__" ? (e.newCategory || "").trim() : e.category;
      if (cat.length < 2) {
        toast.error("Enter a category name");
        return;
      }
      payload.category = cat;
    }
    if (Object.keys(payload).length === 1) return;
    try {
      await axios.patch(`${API}/menu/item`, payload, { headers });
      toast.success(`${item.name} updated`);
      setEdits((prev) => {
        const n = { ...prev };
        delete n[item.name];
        return n;
      });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update item");
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/menu/upload`, fd, { headers });
      const url = res.data.url;
      if (uploadTarget.current) {
        await axios.patch(`${API}/menu/item`, { name: uploadTarget.current, image: url }, { headers });
        toast.success("Photo updated");
        setImageFor(null);
        refresh();
      } else {
        setNewItem((prev) => ({ ...prev, image: url }));
        toast.success("Photo uploaded — will be used for the new dish");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (rowFileRef.current) rowFileRef.current.value = "";
      if (newFileRef.current) newFileRef.current.value = "";
    }
  };

  const saveImageUrl = async (item) => {
    if (!imageUrl.trim()) return;
    try {
      await axios.patch(`${API}/menu/item`, { name: item.name, image: imageUrl.trim() }, { headers });
      toast.success("Photo updated");
      setImageFor(null);
      setImageUrl("");
      refresh();
    } catch {
      toast.error("Could not update photo");
    }
  };

  const toggleAvailability = async (item) => {
    try {
      const next = item.available === false;
      await axios.patch(`${API}/menu/availability`, { name: item.name, available: next }, { headers });
      toast.success(next ? `${item.name} is back on` : `${item.name} marked sold out`);
      refresh();
    } catch {
      toast.error("Could not update availability");
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Remove "${item.name}" from the menu? Guests won't see it anymore.`)) return;
    try {
      await axios.delete(`${API}/menu/item/${encodeURIComponent(item.name)}`, { headers });
      toast.success(`${item.name} removed from the menu`);
      refresh();
    } catch {
      toast.error("Could not delete item");
    }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    const category = newItem.category === "__new__" ? newItem.newCategory.trim() : newItem.category;
    const price = parseInt(newItem.price, 10);
    if (!newItem.name.trim() || !category || !price || price <= 0) {
      toast.error("Name, category and a valid price are required");
      return;
    }
    try {
      await axios.post(
        `${API}/menu/item`,
        { name: newItem.name.trim(), desc: newItem.desc.trim(), price, category, image: newItem.image },
        { headers }
      );
      toast.success(`${newItem.name} added to the menu`);
      setNewItem({ name: "", desc: "", price: "", category: "", newCategory: "", image: "" });
      setAdding(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add item");
    }
  };

  const categoryOptions = [...new Set([...categories, ...items.map((i) => i.cat)])];

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3 transition-colors focus-within:border-[#F57C00]/50 sm:max-w-md">
          <Search size={16} className="shrink-0 text-zinc-500" />
          <input
            data-testid="admin-menu-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search menu items..."
            className="w-full bg-transparent text-sm text-zinc-100 placeholder-zinc-500 outline-none"
          />
        </div>
        <button
          data-testid="add-dish-button"
          onClick={() => setAdding(!adding)}
          className="flex items-center gap-2 rounded-full bg-[#F57C00] px-5 py-3 font-grotesk text-sm font-semibold text-black transition-all hover:bg-[#FF9800]"
        >
          {adding ? <X size={15} /> : <Plus size={15} />}
          {adding ? "Cancel" : "Add New Dish"}
        </button>
      </div>
      <p className="mt-3 text-xs text-zinc-500">
        Tap a photo to change it (paste a link or upload), edit price or category and hit Save — the website, cart and billing update instantly.
      </p>

      {adding && (
        <form
          data-testid="add-dish-form"
          onSubmit={submitNew}
          className="card-surface mt-4 grid grid-cols-1 gap-4 rounded-2xl p-5 sm:grid-cols-2"
        >
          <input
            data-testid="add-dish-name-input"
            required
            value={newItem.name}
            onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
            placeholder="Dish name (e.g. Podi Uttapam)"
            className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-100 placeholder-zinc-500 outline-none focus:border-[#F57C00]/60"
          />
          <input
            data-testid="add-dish-price-input"
            required
            type="number"
            min="1"
            value={newItem.price}
            onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
            placeholder="Price in ₹"
            className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-100 placeholder-zinc-500 outline-none focus:border-[#F57C00]/60"
          />
          <div className="flex gap-2">
            <select
              data-testid="add-dish-category-select"
              required
              value={newItem.category}
              onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
              className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-100 outline-none [color-scheme:dark] focus:border-[#F57C00]/60"
            >
              <option value="" disabled className="bg-[#1A1A1E]">Category</option>
              {categoryOptions.map((c) => (
                <option key={c} value={c} className="bg-[#1A1A1E]">{c}</option>
              ))}
              <option value="__new__" className="bg-[#1A1A1E]">+ New category…</option>
            </select>
          </div>
          {newItem.category === "__new__" ? (
            <input
              data-testid="add-dish-new-category-input"
              required
              value={newItem.newCategory}
              onChange={(e) => setNewItem({ ...newItem, newCategory: e.target.value })}
              placeholder="New category name"
              className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-100 placeholder-zinc-500 outline-none focus:border-[#F57C00]/60"
            />
          ) : (
            <input
              data-testid="add-dish-desc-input"
              value={newItem.desc}
              onChange={(e) => setNewItem({ ...newItem, desc: e.target.value })}
              placeholder="Short description (optional)"
              className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-100 placeholder-zinc-500 outline-none focus:border-[#F57C00]/60"
            />
          )}
          <div className="flex items-center gap-2 sm:col-span-2">
            <input
              data-testid="add-dish-image-url-input"
              value={newItem.image}
              onChange={(e) => setNewItem({ ...newItem, image: e.target.value })}
              placeholder="Photo link (optional) — or upload →"
              className="min-w-0 flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-100 placeholder-zinc-500 outline-none focus:border-[#F57C00]/60"
            />
            <input
              ref={newFileRef}
              data-testid="add-dish-image-upload-input"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={(e) => {
                uploadTarget.current = null;
                uploadImage(e.target.files?.[0]);
              }}
            />
            <button
              type="button"
              data-testid="add-dish-upload-button"
              disabled={uploading}
              onClick={() => newFileRef.current?.click()}
              className="flex shrink-0 items-center gap-2 rounded-xl border border-[#F57C00]/40 px-4 py-3 font-grotesk text-sm font-medium text-[#FF9800] transition-colors hover:bg-[#F57C00] hover:text-black disabled:opacity-50"
            >
              <Upload size={15} /> {uploading ? "Uploading..." : "Upload"}
            </button>
            {newItem.image && (
              <img src={newItem.image} alt="preview" className="h-11 w-11 shrink-0 rounded-lg object-cover" />
            )}
          </div>
          <button
            data-testid="add-dish-submit-button"
            type="submit"
            className="rounded-full bg-[#F57C00] py-3 font-grotesk text-sm font-semibold text-black transition-all hover:bg-[#FF9800] sm:col-span-2"
          >
            Add to Menu
          </button>
        </form>
      )}

      <input
        ref={rowFileRef}
        data-testid="row-image-upload-input"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => uploadImage(e.target.files?.[0])}
      />

      <div className="card-surface mt-4 overflow-hidden rounded-2xl">
        {visible.map((item) => {
          const e = edits[item.name] || {};
          const off = item.available === false;
          const dirty =
            (e.price !== undefined && parseInt(e.price, 10) !== item.price) ||
            (e.category !== undefined && e.category !== item.cat);
          return (
            <div key={item.name} data-testid={`menu-admin-row-${slug(item.name)}`} className="border-b border-white/5 px-5 py-3.5 last:border-0">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  data-testid={`menu-item-thumb-${slug(item.name)}`}
                  onClick={() => {
                    setImageFor(imageFor === item.name ? null : item.name);
                    setImageUrl("");
                  }}
                  className="group relative shrink-0"
                  title="Change photo"
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="h-12 w-12 rounded-lg object-cover"
                  />
                  <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">
                    <ImagePlus size={15} className="text-[#FF9800]" />
                  </span>
                </button>
                <div className="min-w-0 flex-1">
                  <p className={`truncate font-grotesk text-sm font-semibold ${off ? "text-zinc-500 line-through" : "text-zinc-100"}`}>
                    {item.name}
                  </p>
                  <p className="font-mono-accent text-[11px] text-zinc-500">{item.cat}</p>
                </div>
                <select
                  data-testid={`item-category-select-${slug(item.name)}`}
                  value={e.category ?? item.cat}
                  onChange={(ev) => setEdit(item.name, { category: ev.target.value })}
                  className="max-w-[140px] rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-xs text-zinc-200 outline-none [color-scheme:dark]"
                >
                  {categoryOptions.map((c) => (
                    <option key={c} value={c} className="bg-[#1A1A1E]">{c}</option>
                  ))}
                  <option value="__new__" className="bg-[#1A1A1E]">+ New…</option>
                </select>
                {e.category === "__new__" && (
                  <input
                    data-testid={`item-new-category-${slug(item.name)}`}
                    value={e.newCategory || ""}
                    onChange={(ev) => setEdit(item.name, { newCategory: ev.target.value })}
                    placeholder="Category name"
                    className="w-28 rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-xs text-zinc-100 outline-none focus:border-[#F57C00]/60"
                  />
                )}
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-zinc-500">₹</span>
                  <input
                    data-testid={`price-input-${slug(item.name)}`}
                    type="number"
                    min="1"
                    value={e.price ?? item.price}
                    onChange={(ev) => setEdit(item.name, { price: ev.target.value })}
                    className="w-20 rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-right font-mono-accent text-sm text-zinc-100 outline-none focus:border-[#F57C00]/60"
                  />
                </div>
                {dirty && (
                  <button
                    data-testid={`menu-item-save-${slug(item.name)}`}
                    onClick={() => saveItem(item)}
                    className="rounded-full bg-[#F57C00] px-3.5 py-1.5 font-grotesk text-xs font-semibold text-black hover:bg-[#FF9800]"
                  >
                    Save
                  </button>
                )}
                <button
                  data-testid={`availability-toggle-${slug(item.name)}`}
                  onClick={() => toggleAvailability(item)}
                  className={`shrink-0 rounded-full px-4 py-2 font-grotesk text-xs font-semibold transition-all ${
                    off
                      ? "bg-red-500/15 text-red-400 ring-1 ring-red-500/40 hover:bg-red-500/25"
                      : "bg-[#388E3C]/15 text-[#81C784] ring-1 ring-[#4CAF50]/40 hover:bg-[#388E3C]/25"
                  }`}
                >
                  {off ? "Sold Out" : "Available"}
                </button>
                <button
                  data-testid={`delete-item-${slug(item.name)}`}
                  onClick={() => deleteItem(item)}
                  className="rounded-full border border-white/10 p-2 text-zinc-500 transition-colors hover:border-red-400/50 hover:text-red-400"
                  aria-label={`Delete ${item.name}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              {imageFor === item.name && (
                <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/5 p-3">
                  <input
                    data-testid={`image-url-input-${slug(item.name)}`}
                    value={imageUrl}
                    onChange={(ev) => setImageUrl(ev.target.value)}
                    placeholder="Paste photo link (https://...)"
                    className="min-w-0 flex-1 rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-xs text-zinc-100 placeholder-zinc-500 outline-none focus:border-[#F57C00]/60"
                  />
                  <button
                    data-testid={`image-url-save-${slug(item.name)}`}
                    onClick={() => saveImageUrl(item)}
                    className="rounded-full bg-[#F57C00] px-4 py-2 font-grotesk text-xs font-semibold text-black hover:bg-[#FF9800]"
                  >
                    Use Link
                  </button>
                  <button
                    data-testid={`image-upload-button-${slug(item.name)}`}
                    disabled={uploading}
                    onClick={() => {
                      uploadTarget.current = item.name;
                      rowFileRef.current?.click();
                    }}
                    className="flex items-center gap-2 rounded-full border border-[#F57C00]/40 px-4 py-2 font-grotesk text-xs font-medium text-[#FF9800] hover:bg-[#F57C00] hover:text-black disabled:opacity-50"
                  >
                    <Upload size={13} /> {uploading ? "Uploading..." : "Upload Photo"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MenuManager;
